import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { CONTACT_DOCUMENT_TYPE } from './contants/contacts';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function* pages() {
  let currentPage = 1;
  // biome-ignore lint/nursery/noUnnecessaryConditions: yield method.
  while (true) {
    yield currentPage;
    currentPage++;
  }
}

export function formatPrice(price: number) {
  return price.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

export function formatDiscount(
  price: number,
  discountType: 'FIXED' | 'PERCENTAGE' | null
) {
  return discountType === 'FIXED'
    ? price.toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      })
    : (price / 100).toLocaleString('pt-BR', {
        style: 'percent',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
}

/**
 * Parses a date-only string (YYYY-MM-DD) without timezone issues
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Date object set to noon UTC to avoid timezone shifts
 */
export function parseDateOnly(dateString: string): Date {
  // Parse YYYY-MM-DD and set to noon UTC to avoid timezone issues
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
}

/**
 * Formats a date string or Date object to pt-BR format
 * @param date - Date object or date string (YYYY-MM-DD)
 */
export function formatDate(date: Date | string) {
  const dateObj = typeof date === 'string' ? parseDateOnly(date) : date;
  return dateObj.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function formatTimestamp(date: Date) {
  return date.toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

/**
 * Formats a Date as a pt-BR date string in the Brazil timezone.
 * Use for dates interpolated into notification/email TEXT on the server,
 * where the server runs in UTC. NOT for date-only (YYYY-MM-DD) sources —
 * use `formatDate` on the source string instead.
 */
export const formatDateBR = (d: Date) =>
  d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });

/**
 * Formats a Date as a pt-BR date+time string in the Brazil timezone.
 * Same server-side caveats as `formatDateBR`.
 */
export const formatDateTimeBR = (d: Date) =>
  d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });

const cpfRegex = /^(\d{3})(\d{3})(\d{3})(\d{2})$/;
const cnpjRegex = /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/;

// Brasil aboliu horário de verão em 2019 — offset fixo de -3h é seguro.
const BRAZIL_OFFSET_HOURS = 3; // UTC-3 → precisa somar 3h para converter BRT→UTC

export function getBrazilDayRange(date: Date = new Date()): {
  start: Date;
  end: Date;
} {
  const brazilNow = new Date(
    date.getTime() - BRAZIL_OFFSET_HOURS * 60 * 60 * 1000
  );
  const year = brazilNow.getUTCFullYear();
  const month = brazilNow.getUTCMonth();
  const day = brazilNow.getUTCDate();
  // 00:00:00.000 BRT = 03:00:00.000 UTC
  const start = new Date(
    Date.UTC(year, month, day, BRAZIL_OFFSET_HOURS, 0, 0, 0)
  );
  // 23:59:59.999 BRT = 02:59:59.999 UTC do dia seguinte
  const end = new Date(
    Date.UTC(year, month, day + 1, BRAZIL_OFFSET_HOURS - 1, 59, 59, 999)
  );
  return { start, end };
}

export function getBrazilMonthRange(date: Date = new Date()): {
  start: Date;
  end: Date;
} {
  const brazilNow = new Date(
    date.getTime() - BRAZIL_OFFSET_HOURS * 60 * 60 * 1000
  );
  const year = brazilNow.getUTCFullYear();
  const month = brazilNow.getUTCMonth();
  // 1º dia do mês 00:00 BRT = 03:00 UTC
  const start = new Date(
    Date.UTC(year, month, 1, BRAZIL_OFFSET_HOURS, 0, 0, 0)
  );
  // Último dia do mês 23:59:59.999 BRT = 1º dia do mês seguinte 02:59:59.999 UTC
  const end = new Date(
    Date.UTC(year, month + 1, 1, BRAZIL_OFFSET_HOURS - 1, 59, 59, 999)
  );
  return { start, end };
}

export function formatDocument(
  documentType: CONTACT_DOCUMENT_TYPE,
  document: string
) {
  switch (documentType) {
    case CONTACT_DOCUMENT_TYPE.CPF:
      return cpfRegex.test(document)
        ? document.replace(cpfRegex, '$1.$2.$3-$4')
        : document;
    case CONTACT_DOCUMENT_TYPE.CNPJ:
      return cnpjRegex.test(document)
        ? document.replace(cnpjRegex, '$1.$2.$3/$4-$5')
        : document;
    default:
      return document;
  }
}
